/* eslint-disable react-hooks/exhaustive-deps */
import { AgGridReact } from 'ag-grid-react';
import { useEffect, useMemo, useState, useRef, useImperativeHandle, forwardRef } from 'react';
import Select from 'react-select';
import * as XLSX from 'xlsx';
import MeInvoiceApi from '../services/meinvoice';
import {
  Badge,
  Button,
  ButtonGroup,
  Col,
  Container,
  Form,
  Modal,
  Row,
  Stack,
  Table,
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import DrivingServiceInstance from '../services/driving';
import { MEINVOICE_SYNC_LABEL } from '../constants/meinvoice.constant';
import AdminLogoutButton from '../components/AdminLogoutButton';

function MeInvoicePage() {
  const [searchText, setSearchText] = useState('');
  const [gridApi, setGridApi] = useState(null);
  const [initialSearchQuery, setInitialSearchQuery] = useState(null);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [syncing, setSyncing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [drivingTeachers, setDrivingTeachers] = useState([]);
  const [teachersLoaded, setTeachersLoaded] = useState(false);
  const [showDetailInvoiceModal, setShowDetailInvoiceModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exporting, setExporting] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [markedForNoBatching, setMarkedForNoBatching] = useState(null);

  // Cảnh báo cùng NCC + người mua + ngày vượt 5tr
  const OVER_THRESHOLD_AMOUNT = 5000000;
  const [showOverThresholdModal, setShowOverThresholdModal] = useState(false);
  const [overThresholdLoading, setOverThresholdLoading] = useState(false);
  const [overThresholdGroups, setOverThresholdGroups] = useState([]);
  const [overThresholdScannedCount, setOverThresholdScannedCount] = useState(0);
  const [overThresholdHasMore, setOverThresholdHasMore] = useState(false);
  const [overThresholdProgress, setOverThresholdProgress] = useState({ current: 0, total: 0 });
  const [overThresholdStage, setOverThresholdStage] = useState(''); // 'fetching' | 'checking' | ''
  const [overThresholdScanned, setOverThresholdScanned] = useState(false);
  // Bộ lọc riêng cho modal (không ảnh hưởng grid chính)
  const todayStr = new Date().toISOString().split('T')[0];
  const [scanStartDate, setScanStartDate] = useState(todayStr);
  const [scanEndDate, setScanEndDate] = useState(todayStr);
  const [scanSellerTaxCode, setScanSellerTaxCode] = useState('');
  const [scanBuyerTaxCode, setScanBuyerTaxCode] = useState('');
  const [scanThreshold, setScanThreshold] = useState(OVER_THRESHOLD_AMOUNT);

  // Advanced filter state
  const [showFilter, setShowFilter] = useState(false);
  const [filterDrivingTeacher, setFilterDrivingTeacher] = useState(null);
  const [filterDetectedItemType, setFilterDetectedItemType] = useState('');
  const [filterMarkedForNoBatching, setFilterMarkedForNoBatching] = useState('');
  const [filterIsSplit, setFilterIsSplit] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState(new Date().toISOString().split('T')[0]);

  const showDetailInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setShowDetailInvoiceModal(true);
  };

  const DRIVING_TEACHER_OPTIONS = useMemo(() => {
    // Use teacher code as the option value/label; fallback to _id when code missing
    const teacherCodes = Array.isArray(drivingTeachers)
      ? drivingTeachers
          .map((teacher) => teacher?.code || teacher?._id || '')
          .filter(Boolean)
      : [];
    return teacherCodes;
  }, [drivingTeachers]);

  const buildFilters = () => {
    const filters = {};
    if (filterDrivingTeacher?.value) filters.DrivingTeacher = filterDrivingTeacher.value;
    if (filterDetectedItemType) filters.DetectedItemType = filterDetectedItemType;
    if (filterMarkedForNoBatching !== '') filters.MarkedForNoBatching = filterMarkedForNoBatching;
    if (filterIsSplit !== '') filters.IsSplit = filterIsSplit;
    if (filterStartDate && filterEndDate) {
      const startISO = new Date(filterStartDate + 'T00:00:00.000Z').toISOString();
      const endISO = new Date(filterEndDate + 'T23:59:59.999Z').toISOString();
      filters.createdAt = { $gte: startISO, $lte: endISO };
    }
    return filters;
  };

  // track invoices updated directly from the editor to avoid duplicate API calls
  const pendingEditorUpdatesRef = useRef(new Set());

  const DETECTED_ITEM_TYPE_OPTIONS = useMemo(() => {
    return [
      '',
      'GASOLINE',
      'DIESEL',
      'OTHER',
    ];
  }, []);

  const handleDrivingTeacherChange = async (selectedOption, ctx = {}) => {
    // selectedOption: { value, label } or null
    // ctx: { invoiceId, field, node }
    const newVal = selectedOption ? selectedOption.value : '';
    const invoiceId = ctx.invoiceId;
    const field = ctx.field || 'DrivingTeacher';

    if (!invoiceId) return; // nothing to update

    // optimistic update: apply new value to grid immediately, then call API
    const oldVal = ctx.node?.data ? ctx.node.data[field] : undefined;

    try {
      pendingEditorUpdatesRef.current.add(invoiceId);
    } catch (e) {}

    if (ctx.node && typeof ctx.node.setDataValue === 'function') {
      try {
        try { ctx.node.data[field] = newVal; } catch (e) {}
        try { ctx.node.setDataValue(field, newVal); } catch (e) {}
        try { ctx.node.data.__editorUpdated = true; } catch (e) {}

        const apiToUse = ctx.api || gridApi;
        try {
          if (apiToUse && typeof apiToUse.refreshCells === 'function') {
            apiToUse.refreshCells({ rowNodes: [ctx.node], force: true });
          }
          if (apiToUse && typeof apiToUse.redrawRows === 'function') {
            apiToUse.redrawRows({ rowNodes: [ctx.node] });
          }
        } catch (e) {}
      } catch (e) {}
    }

    try {
      await MeInvoiceApi.updateMeInvoice(invoiceId, { [field]: newVal });
      toast.success('Cập nhật thành công');

      // ensure infinite cache reloads from server so the latest value is used
      try {
        const apiToUse = ctx.api || gridApi;
        if (apiToUse && typeof apiToUse.refreshInfiniteCache === 'function') {
          apiToUse.refreshInfiniteCache();
        }
      } catch (e) {}
    } catch (error) {
      console.error('Update driving teacher failed', error);
      toast.error('Cập nhật thất bại');
      // revert UI to old value
      if (ctx.node && typeof ctx.node.setDataValue === 'function') {
        try {
          try { ctx.node.data[field] = oldVal; } catch (e) {}
          try { ctx.node.setDataValue(field, oldVal); } catch (e) {}
          if (gridApi && typeof gridApi.refreshCells === 'function') {
            gridApi.refreshCells({ rowNodes: [ctx.node], force: true });
          }
        } catch (e) {}
      }
    } finally {
      try { pendingEditorUpdatesRef.current.delete(invoiceId); } catch (e) {}
    }
  };

  // Custom AG Grid cell editor using react-select for DrivingTeacher selection
  const DrivingTeacherEditor = forwardRef((props, ref) => {
    const { value } = props;
    // prefer options passed via cellEditorParams, fall back to DRIVING_TEACHER_OPTIONS
    const sourceList = Array.isArray(props.options) && props.options.length > 0
      ? props.options
      : DRIVING_TEACHER_OPTIONS || [];
    const options = sourceList
      .filter(Boolean)
      .map((v) => ({ value: v, label: v }));
    const [selected, setSelected] = useState(
      value ? { value: value, label: value } : null
    );
    const selectRef = useRef(null);

    useImperativeHandle(ref, () => ({
      // called by AG Grid to get the final value
      getValue: () => (selected ? selected.value : ''),
      // show as popup so react-select dropdown isn't clipped
      isPopup: () => true,
    }));

    useEffect(() => {
      // focus the control when mounted
      if (selectRef.current && typeof selectRef.current.focus === 'function') {
        try {
          selectRef.current.focus();
        } catch (e) {
          // ignore
        }
      }
    }, []);

    return (
      <Select
        ref={selectRef}
        options={options}
        value={selected}
        onChange={(val) => {
          setSelected(val);
          handleDrivingTeacherChange(val, {
            invoiceId: props.node?.data?._id,
            field: props.colDef?.field,
            node: props.node,
            api: props.api,
          });
        }}
        isClearable
        placeholder=''
        menuPortalTarget={
          typeof document !== 'undefined' ? document.body : undefined
        }
        menuPosition='fixed'
        styles={{
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        }}
      />
    );
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const colDefs = useMemo(
    () => [
      {
        headerName: 'STT',
        valueGetter: (params) =>
          params?.node?.rowIndex != null ? params.node.rowIndex + 1 : '',
        width: 110,
        pinned: 'left',
      },
      {
        field: '_id',
        headerName: 'Thao tác',
        width: 150,
        pinned: 'left',
        cellRenderer: (params) => {
          return (
            <Button onClick={() => showDetailInvoice(params.data)}>Xem</Button>
          );
        },
      },
      {
        field: 'DrivingTeacher',
        headerName: 'Người nhận nhiên liệu',
        width: 150,
        pinned: 'left',
        editable: (params) => {
          return (
            params.data?.InternalPaidAmount === 0 &&
            !params.data?.IsSplit &&
            ['GASOLINE', 'DIESEL'].includes(params.data?.DetectedItemType)
          );
        },
        // use custom react-select based editor and pass options via cellEditorParams
        cellEditor: DrivingTeacherEditor,
        cellEditorParams: {
          options: DRIVING_TEACHER_OPTIONS,
        },
      },
      {
        field: 'DetectedItemType',
        headerName: 'Loại nhiên liệu',
        width: 150,
        editable: (params) => params.data?.Items.length > 0,
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: {
          values:
            DETECTED_ITEM_TYPE_OPTIONS && DETECTED_ITEM_TYPE_OPTIONS.length > 0
              ? DETECTED_ITEM_TYPE_OPTIONS
              : [''],
        },
        cellRenderer: (params) => {
          const type = params.value;
          if (type === 'GASOLINE') return 'Xăng';
          if (type === 'DIESEL') return 'Dầu diesel';
          if (type === 'OTHER') return 'Khác';
          return '';
        },
        cellStyle: (params) => {
          if (
            ['GASOLINE', 'DIESEL'].includes(params.value) &&
            params.data?.TotalAmount > 1500000
          ) {
            return { color: 'red', fontWeight: 'bold' };
          }
          return null;
        },
      },
      {
        field: 'TotalFuelQuantity',
        headerName: 'Tổng số lít',
        width: 130,
        valueGetter: (params) => {
          if (!['GASOLINE', 'DIESEL'].includes(params.data?.DetectedItemType)) return null;
          const items = Array.isArray(params.data?.Items) ? params.data.Items : [];
          return items.reduce((sum, item) => sum + (item.Quantity || 0), 0);
        },
        valueFormatter: (params) => {
          if (params.value == null) return '';
          const items = Array.isArray(params.data?.Items) ? params.data.Items : [];
          const unit = items[0]?.UnitName || 'lít';
          return `${params.value.toLocaleString('vi-VN')} ${unit}`;
        },
        cellStyle: { fontWeight: 'bold' },
      },
      {
        field: 'IsSplit',
        headerName: 'Loại hoá đơn',
        minWidth: 210,
        flex: 1,
        cellRenderer: (params) => {
          return params.data?.ParentInvoiceId
            ? 'Hoá đơn con'
            : params.data?.IsSplit
            ? 'Hoá đơn gốc (có tách)'
            : 'Hoá đơn gốc';
        },
      },
      {
        field: 'InternalPaidAmount',
        headerName: 'Đã chi nhiên liệu',
        minWidth: 150,
        flex: 1,
        cellRenderer: (params) => {
          return params.data?.IsSplit && !params.data?.ParentInvoiceId ? (
            <Button onClick={() => showDetailInvoice(params.data)}>
              Chi tiết
            </Button>
          ) : (
            new Intl.NumberFormat('vi-VN', {
              style: 'currency',
              currency: 'VND',
            }).format(params.data?.InternalPaidAmount || 0)
          );
        },
      },
      {
        field: 'TotalAmount',
        headerName: 'Tổng tiền',
        minWidth: 190,
        flex: 1,
        valueFormatter: (params) => {
          const formatted = new Intl.NumberFormat('vi-VN', {
            style: 'currency',
            currency: 'VND',
          }).format(params.value);
          if (params.value > 5000000) {
            return `${formatted} (vượt 5tr)`;
          }
          if (params.value > 1500000) {
            return `${formatted} (vượt 1.5tr)`;
          }
          return formatted;
        },
        cellStyle: (params) => {
          if (params.value > 5000000) {
            return { color: 'red', fontWeight: 'bold' };
          }
          if (params.value > 1500000) {
            return { color: 'orange', fontWeight: 'bold' };
          }
          return null;
        },
      },
      {
        field: 'UnpaidAmount',
        // cSpell:disable-next-line
        headerName: 'Chưa chi nhiên liệu',
        minWidth: 150,
        flex: 1,
        valueGetter: (params) => {
          return (
            (params.data?.TotalAmount || 0) -
            (params.data?.InternalPaidAmount || 0)
          );
        },
        valueFormatter: (params) => {
          return new Intl.NumberFormat('vi-VN', {
            style: 'currency',
            currency: 'VND',
          }).format(params.value);
        },
        cellStyle: (params) => {
          if (params.value > 0) {
            return { color: 'orange', fontWeight: 'bold' };
          }
          return null;
        },
      },
      {
        field: 'InvoiceNo',
        headerName: 'Số hoá đơn',
        flex: 1,
        minWidth: 150,
      },
      {
        field: 'InvoiceDate',
        headerName: 'Ngày hoá đơn',
        flex: 1,
        minWidth: 150,
        valueFormatter: (params) => {
          return new Date(params.value).toLocaleDateString('vi-VN', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
          });
        },
      },
      {
        field: 'SignedDate',
        headerName: 'Ngày ký',
        flex: 1,
        minWidth: 150,
        valueFormatter: (params) => {
          return new Date(params.value).toLocaleDateString('vi-VN', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
          });
        },
      },
      {
        field: 'LicensePlate',
        headerName: 'Biển số',
        flex: 1,
        minWidth: 150,
      },
      {
        field: 'SellerName',
        headerName: 'Nhà cung cấp',
        minWidth: 350,
        flex: 1,
      },
      {
        field: 'SellerAddress',
        headerName: 'Địa chỉ NCC',
        minWidth: 350,
        flex: 1,
      },
      {
        field: 'SellerTaxCode',
        headerName: 'Mã số thuế NCC',
        minWidth: 150,
        flex: 1,
      },
      {
        field: 'BuyerName',
        headerName: 'Người mua',
        minWidth: 350,
        flex: 1,
      },
      {
        field: 'BuyerAddress',
        headerName: 'Địa chỉ người mua',
        minWidth: 350,
        flex: 1,
      },
      {
        field: 'BuyerTaxCode',
        headerName: 'Mã số thuế người mua',
        minWidth: 150,
        flex: 1,
      },
      {
        field: 'Series',
        headerName: 'Ký hiệu',
        minWidth: 150,
        flex: 1,
      },
      {
        field: 'TotalAmountWithoutVat',
        headerName: 'Thành tiền trước thuế',
        minWidth: 150,
        flex: 1,
        valueFormatter: (params) => {
          return new Intl.NumberFormat('vi-VN', {
            style: 'currency',
            currency: 'VND',
          }).format(params.value);
        },
      },
      {
        field: 'TotalVATAmount',
        headerName: 'Tổng tiền thuế',
        minWidth: 150,
        flex: 1,
        valueFormatter: (params) => {
          return new Intl.NumberFormat('vi-VN', {
            style: 'currency',
            currency: 'VND',
          }).format(params.value);
        },
      },
      {
        field: 'StatusInvoices',
        headerName: 'Trạng thái',
        minWidth: 350,
        flex: 1,
        cellRenderer: (params) => {
          const statuses = Array.isArray(params.data?.StatusInvoices)
            ? params.data.StatusInvoices
            : [];
          return (
            <Stack direction='horizontal'>
              {statuses.map((status) => (
                <Badge
                  className='p-2 m-2'
                  key={status.Key}
                  bg={status.Key === 'mi-invoice-valid' ? 'success' : 'warning'}
                >
                  {status.Status}
                </Badge>
              ))}
            </Stack>
          );
        },
      },
    ],
    [DRIVING_TEACHER_OPTIONS]
  );

  const onGridReady = (params) => {
    setGridApi(params.api);
    const dataSource = getDataSource();
    params.api.setGridOption('datasource', dataSource);
  };

  const getDataSource = (search, filters = {}) => {
    return {
      rowCount: null,
      getRows: async (params) => {
        const { startRow, endRow } = params;
        try {
          const page = Math.floor(startRow / (endRow - startRow)) + 1;
          const limit = endRow - startRow;
          const res = await MeInvoiceApi.listMeInvoices(page, limit, search, filters);
          params.successCallback(res.data, res.pagination.totalDocs);
        } catch (error) {
          params.failCallback();
        }
      },
    };
  };

  const formatVnd = (amount) =>
    new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
    }).format(amount || 0);

  const getInvoiceDateKey = (invoice) => {
    const raw = invoice?.InvoiceDate;
    if (!raw) return '';
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return '';
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const openOverThresholdModal = () => {
    setShowOverThresholdModal(true);
    setOverThresholdScanned(false);
    setOverThresholdGroups([]);
    setOverThresholdScannedCount(0);
    setOverThresholdHasMore(false);
    setOverThresholdProgress({ current: 0, total: 0 });
    setOverThresholdStage('');
  };

  const runOverThresholdScan = async () => {
    if (!scanStartDate || !scanEndDate) {
      toast.error('Vui lòng chọn khoảng thời gian quét');
      return;
    }
    if (new Date(scanStartDate) > new Date(scanEndDate)) {
      toast.error('Ngày bắt đầu không được lớn hơn ngày kết thúc');
      return;
    }
    const threshold = Number(scanThreshold);
    if (!Number.isFinite(threshold) || threshold <= 0) {
      toast.error('Ngưỡng cảnh báo phải là số dương');
      return;
    }

    setOverThresholdLoading(true);
    setOverThresholdScanned(true);
    setOverThresholdGroups([]);
    setOverThresholdScannedCount(0);
    setOverThresholdHasMore(false);
    setOverThresholdProgress({ current: 0, total: 0 });
    setOverThresholdStage('fetching');

    const PAGE_SIZE = 200;
    const MAX_PAGES = 25; // tối đa 5.000 hoá đơn
    const MAX_PAIRS = 500;
    const CONCURRENCY = 8;
    const REQUEST_TIMEOUT_MS = 15000;

    // Bộ lọc dùng riêng cho modal: lọc theo InvoiceDate (ngày hoá đơn) thay vì createdAt
    // Mốc giờ Asia/Ho_Chi_Minh (+07:00) để khớp với cách backend chia ngày
    const startISO = new Date(scanStartDate + 'T00:00:00+07:00').toISOString();
    const endISO = new Date(scanEndDate + 'T23:59:59.999+07:00').toISOString();
    const filters = {
      InvoiceDate: { $gte: startISO, $lte: endISO },
    };
    if (scanSellerTaxCode.trim()) filters.SellerTaxCode = scanSellerTaxCode.trim();
    if (scanBuyerTaxCode.trim()) filters.BuyerTaxCode = scanBuyerTaxCode.trim();

    const collected = [];

    const withTimeout = (promise, ms) =>
      Promise.race([
        promise,
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Timeout')), ms)
        ),
      ]);

    try {
      for (let page = 1; page <= MAX_PAGES; page += 1) {
        const res = await MeInvoiceApi.listMeInvoices(page, PAGE_SIZE, '', filters);
        const rows = Array.isArray(res?.data) ? res.data : [];
        collected.push(...rows);
        setOverThresholdScannedCount(collected.length);
        const totalDocs = res?.pagination?.totalDocs ?? collected.length;
        if (collected.length >= totalDocs || rows.length < PAGE_SIZE) break;
        if (page === MAX_PAGES && collected.length < totalDocs) {
          setOverThresholdHasMore(true);
        }
      }

      // Lấy các cặp (Seller + Buyer + Date) duy nhất
      const pairMap = new Map();
      for (const inv of collected) {
        const sellerTax = (inv?.SellerTaxCode || '').trim();
        const buyerTax = (inv?.BuyerTaxCode || '').trim();
        const dateKey = getInvoiceDateKey(inv);
        if (!sellerTax || !buyerTax || !dateKey) continue;
        const key = `${sellerTax}|${buyerTax}|${dateKey}`;
        if (!pairMap.has(key)) {
          pairMap.set(key, {
            key,
            sellerTaxCode: sellerTax,
            sellerName: inv?.SellerName || '',
            buyerTaxCode: buyerTax,
            buyerName: inv?.BuyerName || '',
            dateKey,
          });
        }
      }

      let pairs = [...pairMap.values()];
      if (pairs.length > MAX_PAIRS) {
        setOverThresholdHasMore(true);
        pairs = pairs.slice(0, MAX_PAIRS);
      }

      setOverThresholdStage('checking');
      setOverThresholdProgress({ current: 0, total: pairs.length });

      const results = [];
      let cursor = 0;
      let completed = 0;

      const worker = async () => {
        while (cursor < pairs.length) {
          const idx = cursor;
          cursor += 1;
          const pair = pairs[idx];
          try {
            const res = await withTimeout(
              MeInvoiceApi.checkDailyAmount({
                sellerTaxCode: pair.sellerTaxCode,
                buyerTaxCode: pair.buyerTaxCode,
                date: pair.dateKey,
                threshold,
              }),
              REQUEST_TIMEOUT_MS
            );
            const data = res?.data;
            if (data?.exceeded) {
              results.push({
                ...pair,
                total: Number(data.totalAmount || data.existingTotal || 0),
                invoiceCount: data.invoiceCount || 0,
                invoices: Array.isArray(data.invoices) ? data.invoices : [],
              });
            }
          } catch (err) {
            console.error('checkDailyAmount failed for pair', pair.key, err);
          } finally {
            completed += 1;
            setOverThresholdProgress({ current: completed, total: pairs.length });
          }
        }
      };

      const workerCount = Math.min(CONCURRENCY, pairs.length);
      if (workerCount > 0) {
        await Promise.all(
          Array.from({ length: workerCount }, () => worker())
        );
      }

      results.sort((a, b) => b.total - a.total);

      setOverThresholdGroups(results);
    } catch (error) {
      console.error('Check over threshold failed', error);
      toast.error('Kiểm tra thất bại');
    } finally {
      setOverThresholdStage('');
      setOverThresholdLoading(false);
    }
  };

  const searchInvoices = async () => {
    if (filterStartDate && filterEndDate && new Date(filterStartDate) > new Date(filterEndDate)) {
      toast.error('Ngày bắt đầu không được lớn hơn ngày kết thúc');
      return;
    }

    setLoading(true);
    try {
      if (gridApi) {
        const dataSource = getDataSource(searchText, buildFilters());
        gridApi.setGridOption('datasource', dataSource);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const onCellValueChanged = async (params) => {

    // If this row was updated by the editor handler, skip duplicate update
    if (params.node?.data && params.node.data.__editorUpdated) {
      try {
        delete params.node.data.__editorUpdated;
      } catch (e) {}
      return;
    }

    // If this invoice is currently being updated by the editor handler, skip duplicate update
    if (pendingEditorUpdatesRef.current && pendingEditorUpdatesRef.current.has(params.data?._id)) {
      // consume the pending flag and skip
      pendingEditorUpdatesRef.current.delete(params.data._id);
      return;
    }

    if (params.data[params.colDef.field] === params.oldValue) {
      return;
    }

    try {
      await MeInvoiceApi.updateMeInvoice(params.data._id, {
        [params.colDef.field]: params.data[params.colDef.field],
      });
      toast.success('Cập nhật thành công');
    } catch (error) {
      console.error(error);
      toast.error('Cập nhật thất bại');
      params.data[params.colDef.field] = params.oldValue; // revert change
      params.api.refreshCells({ rowNodes: [params.node] });
    }
  };

  const exportInvoices = async (chuaChi = false) => {
    if (!filterStartDate || !filterEndDate) {
      toast.error('Vui lòng chọn khoảng thời gian trong bộ lọc để xuất báo cáo');
      return;
    }

    const startDateObj = new Date(filterStartDate);
    const endDateObj = new Date(filterEndDate);

    if (startDateObj > endDateObj) {
      toast.error('Ngày bắt đầu không được lớn hơn ngày kết thúc');
      return;
    }

    setExporting(true);
    try {
      const query = { ...buildFilters() };

      if (searchText?.trim()) {
        query.search = searchText.trim();
      }

      const response = await MeInvoiceApi.exportMeInvoices({ query, chuaChi });

      const contentDisposition = response.headers['content-disposition'] || '';
      const filenameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/);
      const filename = filenameMatch ? filenameMatch[1] : `meinvoices_${Date.now()}.xlsx`;

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success('Đã xuất báo cáo');
      setShowExportModal(false);
    } catch (error) {
      console.error('Export invoices error', error);
      toast.error(error?.response?.data?.message || 'Xuất báo cáo thất bại');
    } finally {
      setExporting(false);
    }
  };

  const handleMarkForNoBatching = async (id, value) => {
    try {
      await MeInvoiceApi.updateMeInvoice(id, {
        MarkedForNoBatching: value,
      });
      toast.success('Đã đánh dấu hoá đơn không nhóm');
      // Refresh the grid after update
      if (gridApi) {
        gridApi.refreshInfiniteCache();
      }
    } catch (error) {
      toast.error('Cập nhật thất bại');
    } finally {
      setShowDetailInvoiceModal(false);
    }
  };

  // Đánh dấu/bỏ đánh dấu trực tiếp từ modal cảnh báo, cập nhật state cục bộ
  const [markingNoBatchInvoiceId, setMarkingNoBatchInvoiceId] = useState(null);
  const handleMarkInvoiceFromOverThreshold = async (invoiceId, nextValue) => {
    if (!invoiceId) return;
    setMarkingNoBatchInvoiceId(invoiceId);
    try {
      await MeInvoiceApi.updateMeInvoice(invoiceId, {
        MarkedForNoBatching: nextValue,
      });
      setOverThresholdGroups((groups) =>
        groups.map((g) => ({
          ...g,
          invoices: g.invoices.map((inv) =>
            inv._id === invoiceId ? { ...inv, MarkedForNoBatching: nextValue } : inv
          ),
        }))
      );
      if (gridApi) {
        gridApi.refreshInfiniteCache();
      }
      toast.success(nextValue ? 'Đã đánh dấu không nhóm' : 'Đã bỏ đánh dấu không nhóm');
    } catch (error) {
      console.error('Mark for no batching failed', error);
      toast.error('Cập nhật thất bại');
    } finally {
      setMarkingNoBatchInvoiceId(null);
    }
  };

  const exportOverThresholdToExcel = () => {
    if (!overThresholdGroups || overThresholdGroups.length === 0) return;

    const summaryData = overThresholdGroups.map((group, idx) => ({
      STT: idx + 1,
      'Mã số thuế NCC': group.sellerTaxCode,
      'Tên NCC': group.sellerName || '-',
      'Mã số thuế người mua': group.buyerTaxCode,
      'Tên người mua': group.buyerName || '-',
      'Ngày hoá đơn': group.dateKey,
      'Tổng tiền': group.total,
      'Số hoá đơn': group.invoiceCount,
    }));

    const detailData = [];
    overThresholdGroups.forEach((group) => {
      (group.invoices || []).forEach((inv, idx) => {
        detailData.push({
          STT: detailData.length + 1,
          'Số hoá đơn': inv.InvoiceNo || '-',
          'Ký hiệu': inv.Series || '-',
          'Tổng tiền': inv.TotalAmount || 0,
          'Loại nhiên liệu': inv.DetectedItemType || '-',
          'Biển số': inv.LicensePlate || '-',
          'Mã số thuế NCC': group.sellerTaxCode,
          'Tên NCC': group.sellerName || '-',
          'Mã số thuế người mua': group.buyerTaxCode,
          'Tên người mua': group.buyerName || '-',
          'Ngày hoá đơn': group.dateKey,
          'Đánh dấu không nhóm': inv.MarkedForNoBatching ? 'Có' : 'Không',
        });
      });
    });

    const wb = XLSX.utils.book_new();
    const wsSummary = XLSX.utils.json_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(wb, wsSummary, 'Tổng hợp');
    const wsDetail = XLSX.utils.json_to_sheet(detailData);
    XLSX.utils.book_append_sheet(wb, wsDetail, 'Chi tiết');

    const now = new Date();
    const ts = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}${String(now.getSeconds()).padStart(2, '0')}`;
    XLSX.writeFile(wb, `kiem-tra-vuot-nguong_${ts}.xlsx`);
    toast.success('Đã xuất báo cáo Excel');
  };

  const syncInvoices = async (syncType) => {
    if (!startDate || !endDate) {
      toast.error('Vui lòng chọn khoảng thời gian để đồng bộ');
      return;
    }

    const startDateObj = new Date(startDate);
    const endDateObj = new Date(endDate);

    if (startDateObj > endDateObj) {
      toast.error('Ngày bắt đầu không được lớn hơn ngày kết thúc');
      return;
    }

    if (
      endDateObj.getTime() - startDateObj.getTime() >
      1000 * 60 * 60 * 24 * 7
    ) {
      toast.error('Khoảng thời gian đồng bộ không được quá 7 ngày');
      return;
    }

    setSyncing(true);
    try {
      if (syncType === 'items') {
        // Chuyển startDate và endDate sang UTC ISO string
        const startUTC = new Date(
          startDateObj.getFullYear(),
          startDateObj.getMonth(),
          startDateObj.getDate(),
          0,
          0,
          0
        );
        const endUTC = new Date(
          endDateObj.getFullYear(),
          endDateObj.getMonth(),
          endDateObj.getDate(),
          23,
          59,
          59
        );

        const startISO = startUTC.toISOString();
        const endISO = endUTC.toISOString();
        const resItems = await MeInvoiceApi.syncMeInvoiceItemsByDateRange(
          startISO,
          endISO
        );
        toast.success(`Đã đồng bộ ${resItems.data.processed} hàng hoá`);
      } else {
        const res = await MeInvoiceApi.syncMeInvoices(startDate+'T00:00:00.000Z', endDate+'T23:59:59.999Z');
        toast.success(`Đã đồng bộ ${res.data.totalInvoicesProcessed} hoá đơn`);
      }

      // Refresh the grid after syncing
      if (gridApi) {
        gridApi.refreshInfiniteCache();
      }
    } catch (error) {
      console.error(error);
      toast.error('Đồng bộ hoá đơn thất bại');
    } finally {
      setSyncing(false);
      setShowSyncModal(false);
    }
  };

  useEffect(() => {
    document.title = 'Quản lý hoá đơn | eDriving';

    const fetchDrivingTeachers = async () => {
      try {
        setLoading(true);
        const res = await DrivingServiceInstance.listInvoiceDrivingTeachers(1, 200);
        setDrivingTeachers(res.data);
        setTeachersLoaded(true);

        // If grid is already initialized, refresh column defs and cells so
        // the custom editor receives the updated options list.
        if (gridApi) {
          try {
            // Re-apply the column definitions (colDefs is memoized and will
            // reflect updated DRIVING_TEACHER_OPTIONS because it depends on it)
            gridApi.setColumnDefs(colDefs);
            // Refresh cells to ensure editors read latest params
            gridApi.refreshCells({ force: true });
          } catch (e) {
            // ignore any refresh errors
            console.warn('AG Grid refresh after loading teachers failed', e);
          }
        }
      } catch (error) {
        console.error('Error fetching driving teachers:', error);
        setDrivingTeachers([]);
        setTeachersLoaded(true);
      } finally {
        setLoading(false);
      }
    };

    fetchDrivingTeachers();
    // Read `search` param from URL and prepare to auto-run the search once grid is ready
    try {
      const params = new URLSearchParams(window.location.search);
      const q = params.get('search');
      if (q) {
        setSearchText(q);
        setInitialSearchQuery(q);
      }
    } catch (e) {
      // ignore
    }
  }, []);

  // When the grid API becomes available and an initial search query exists, run the search
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (gridApi && initialSearchQuery) {
      const dataSource = getDataSource(initialSearchQuery);
      gridApi.setGridOption('datasource', dataSource);
      // clear so it doesn't re-run
      setInitialSearchQuery(null);
      // totals will update when data renders
    }
  }, [gridApi, initialSearchQuery]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (showDetailInvoiceModal) {
      MeInvoiceApi.listInternalMeInvoices(selectedInvoice._id)
        .then((res) => {
          setSelectedInvoice((prev) => ({
            ...prev,
            internalInvoices: res.data,
          }));
        })
        .catch((error) => {
          console.error('Error fetching internal invoices:', error);
        });
    }
  }, [showDetailInvoiceModal]);

  return (
    <>
      <Container
        style={{
          height: '100vh',
        }}
        fluid
      >
        <Row style={{ height: '8%' }} className='align-items-end position-relative'>
          <h2 className='text-center mb-0'>QUẢN LÝ HÓA ĐƠN</h2>
          <div className='position-absolute end-0 bottom-0 pe-3 d-flex gap-2' style={{ width: 'auto' }}>
            {/* App này không có menu chung — điều hướng bằng link trực tiếp.
              * Không có link ở đây thì màn nhập bằng AI không ai tìm ra. */}
            <Button size='sm' variant='outline-primary' href='/admin/meinvoice/ocr'>
              Nhập bằng AI
            </Button>
            <AdminLogoutButton />
          </div>
        </Row>
        <Row style={{ height: '10%' }} className='align-items-center'>
          <Col xs={12} md={7}>
            <Form.Control
              className='mb-3'
              type='text'
              placeholder='Nhập số hoá đơn, tên người nhận nhiên liệu, mã số thuế...'
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
              }}
            />
          </Col>
          <Col xs={12} md={1}>
            <ButtonGroup className='w-100 mb-3'>
              <Button
                onClick={searchInvoices}
                variant='primary'
                disabled={loading}
              >
                Tra cứu
              </Button>
              <Button
                variant='primary'
                onClick={() => setShowFilter((v) => !v)}
                title='Bộ lọc nâng cao'
                style={{ width: '36px', padding: '0', borderLeft: '1px solid rgba(255,255,255,0.3)' }}
              >
                {showFilter ? '▲' : '▼'}
              </Button>
            </ButtonGroup>
          </Col>
          <Col>
            <Button
              onClick={() => setShowSyncModal('invoices')}
              variant='primary'
              disabled={loading}
              className='w-100 mb-3'
            >
              Đồng bộ hoá đơn
            </Button>
          </Col>
          <Col>
            <Button
              onClick={() => setShowSyncModal('items')}
              variant='secondary'
              disabled={loading}
              className='w-100 mb-3'
            >
              Đồng bộ hàng hoá
            </Button>
          </Col>
          <Col>
            <Button
              onClick={() => setShowExportModal(true)}
              variant='success'
              disabled={loading}
              className='w-100 mb-3'
            >
              Xuất Excel
            </Button>
          </Col>
          <Col>
            <Button
              onClick={openOverThresholdModal}
              variant='warning'
              disabled={loading}
              className='w-100 mb-3'
            >
              Kiểm tra
            </Button>
          </Col>
        </Row>
        {showFilter && (
          <Row
            className='mx-0 mb-2 py-2 px-3'
            style={{
              background: '#f0f4f8',
              borderRadius: '8px',
              border: '1px solid #dee2e6',
            }}
          >
            <Col xs={12} md={3}>
              <Form.Group>
                <Form.Label className='fw-semibold mb-1'>Giáo viên</Form.Label>
                <Select
                  options={[
                    { value: '', label: 'Tất cả giáo viên' },
                    ...DRIVING_TEACHER_OPTIONS.map((v) => ({ value: v, label: v })),
                  ]}
                  value={filterDrivingTeacher}
                  onChange={(v) => setFilterDrivingTeacher(v)}
                  isClearable
                  placeholder='Tất cả giáo viên'
                  menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                  menuPosition='fixed'
                  styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={2}>
              <Form.Group>
                <Form.Label className='fw-semibold mb-1'>Loại nhiên liệu</Form.Label>
                <Form.Select
                  value={filterDetectedItemType}
                  onChange={(e) => setFilterDetectedItemType(e.target.value)}
                >
                  <option value=''>Tất cả</option>
                  <option value='GASOLINE'>Xăng</option>
                  <option value='DIESEL'>Dầu diesel</option>
                  <option value='OTHER'>Khác</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={12} md={3}>
              <Form.Group>
                <Form.Label className='fw-semibold mb-1'>Trạng thái nhóm</Form.Label>
                <Form.Select
                  value={filterMarkedForNoBatching}
                  onChange={(e) => setFilterMarkedForNoBatching(e.target.value)}
                >
                  <option value=''>Tất cả</option>
                  <option value='true'>Đánh dấu không nhóm</option>
                  <option value='false'>Chưa đánh dấu</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={12} md={2}>
              <Form.Group>
                <Form.Label className='fw-semibold mb-1'>Loại hoá đơn</Form.Label>
                <Form.Select
                  value={filterIsSplit}
                  onChange={(e) => setFilterIsSplit(e.target.value)}
                >
                  <option value=''>Tất cả</option>
                  <option value='true'>Có tách hoá đơn</option>
                  <option value='false'>Không tách</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={12} md={2}>
              <Form.Group>
                <Form.Label className='fw-semibold mb-1'>Từ ngày tạo</Form.Label>
                <Form.Control
                  type='date'
                  value={filterStartDate}
                  onChange={(e) => setFilterStartDate(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={2}>
              <Form.Group>
                <Form.Label className='fw-semibold mb-1'>Đến ngày tạo</Form.Label>
                <Form.Control
                  type='date'
                  value={filterEndDate}
                  onChange={(e) => setFilterEndDate(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={2} className='d-flex align-items-end'>
              <Button
                variant='outline-secondary'
                className='w-100'
                onClick={async () => {
                  setFilterDrivingTeacher(null);
                  setFilterDetectedItemType('');
                  setFilterMarkedForNoBatching('');
                  setFilterIsSplit('');
                  setFilterStartDate('');
                  setFilterEndDate(new Date().toISOString().split('T')[0]);

                  // Auto refresh data after clearing filters
                  setLoading(true);
                  try {
                    if (gridApi) {
                      const dataSource = getDataSource(searchText, {});
                      gridApi.setGridOption('datasource', dataSource);
                    }
                  } catch (error) {
                    console.error(error);
                  } finally {
                    setLoading(false);
                  }
                }}
              >
                Xóa bộ lọc
              </Button>
            </Col>
          </Row>
        )}
        {teachersLoaded && DRIVING_TEACHER_OPTIONS.length > 0 && (
          <Row className='ag-theme-quartz' style={{ height: showFilter ? 'calc(74% - 140px)' : '74%' }}>
            <AgGridReact
              key={`grid-${drivingTeachers.length}`}
              columnDefs={colDefs}
              defaultColDef={{
                resizable: true,
                sortable: true,
                filter: true,
              }}
              onCellValueChanged={onCellValueChanged}
              pagination={true}
              paginationPageSize={20}
              rowModelType={'infinite'}
              cacheBlockSize={20}
              paginationPageSizeSelector={[10, 20, 50, 100]}
              onGridReady={onGridReady}
              getRowStyle={(params) => {
                if (params.data?.MarkedForNoBatching) {
                  return {
                    backgroundColor: '#ffcdcdff',
                    fontWeight: 'bold',
                  };
                }
                return null;
              }}
            />
          </Row>
        )}
        {(!teachersLoaded || DRIVING_TEACHER_OPTIONS.length === 0) && (
          <Row
            className='justify-content-center align-items-center'
            style={{ height: showFilter ? 'calc(82% - 140px)' : '82%' }}
          >
            <div className='text-center'>
              <div className='spinner-border' role='status'>
                <span className='visually-hidden'>Đang tải...</span>
              </div>
              <p className='mt-2'>Đang tải dữ liệu...</p>
            </div>
          </Row>
        )}
      </Container>
      <Modal
        show={showOverThresholdModal}
        onHide={() => setShowOverThresholdModal(false)}
        size='xl'
        scrollable
        backdrop='static'
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>Kiểm tra hoá đơn vượt ngưỡng/ngày/cặp NCC-người mua</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className='border rounded p-3 mb-3' style={{ background: '#f8f9fa' }}>
            <Row className='g-2'>
              <Col xs={12} md={3}>
                <Form.Label className='small mb-1'>Từ ngày HĐ</Form.Label>
                <Form.Control
                  type='date'
                  value={scanStartDate}
                  onChange={(e) => setScanStartDate(e.target.value)}
                  disabled={overThresholdLoading}
                />
              </Col>
              <Col xs={12} md={3}>
                <Form.Label className='small mb-1'>Đến ngày HĐ</Form.Label>
                <Form.Control
                  type='date'
                  value={scanEndDate}
                  onChange={(e) => setScanEndDate(e.target.value)}
                  disabled={overThresholdLoading}
                />
              </Col>
              <Col xs={12} md={2}>
                <Form.Label className='small mb-1'>MST NCC</Form.Label>
                <Form.Control
                  type='text'
                  placeholder='Tuỳ chọn'
                  value={scanSellerTaxCode}
                  onChange={(e) => setScanSellerTaxCode(e.target.value)}
                  disabled={overThresholdLoading}
                />
              </Col>
              <Col xs={12} md={2}>
                <Form.Label className='small mb-1'>MST người mua</Form.Label>
                <Form.Control
                  type='text'
                  placeholder='Tuỳ chọn'
                  value={scanBuyerTaxCode}
                  onChange={(e) => setScanBuyerTaxCode(e.target.value)}
                  disabled={overThresholdLoading}
                />
              </Col>
              <Col xs={12} md={2}>
                <Form.Label className='small mb-1'>Ngưỡng (VND)</Form.Label>
                <Form.Control
                  type='number'
                  min='0'
                  step='100000'
                  value={scanThreshold}
                  onChange={(e) => setScanThreshold(e.target.value)}
                  disabled={overThresholdLoading}
                />
              </Col>
            </Row>
            <div className='d-flex justify-content-end mt-3'>
              <Button
                variant='primary'
                onClick={runOverThresholdScan}
                disabled={overThresholdLoading}
              >
                {overThresholdLoading ? 'Đang quét...' : 'Quét'}
              </Button>
            </div>
          </div>

          {overThresholdLoading ? (
            <div className='text-center py-4'>
              <div className='spinner-border' role='status'>
                <span className='visually-hidden'>Đang tải...</span>
              </div>
              {overThresholdStage === 'fetching' && (
                <p className='mt-2 mb-0'>
                  Đang tải hoá đơn theo bộ lọc... ({overThresholdScannedCount.toLocaleString('vi-VN')} hoá đơn)
                </p>
              )}
              {overThresholdStage === 'checking' && (
                <>
                  <p className='mt-2 mb-1'>
                    Đang kiểm tra ngưỡng từ server: {overThresholdProgress.current}/{overThresholdProgress.total} cặp NCC-người mua
                  </p>
                  {overThresholdProgress.total > 0 && (
                    <div className='progress mx-auto' style={{ maxWidth: 360, height: 8 }}>
                      <div
                        className='progress-bar'
                        role='progressbar'
                        style={{
                          width: `${Math.round((overThresholdProgress.current / overThresholdProgress.total) * 100)}%`,
                        }}
                      />
                    </div>
                  )}
                </>
              )}
              {!overThresholdStage && (
                <p className='mt-2 mb-0'>Đang quét...</p>
              )}
            </div>
          ) : !overThresholdScanned ? (
            <div className='text-muted text-center py-4'>
              Vui lòng thiết lập bộ lọc và bấm <strong>Quét</strong> để bắt đầu.
            </div>
          ) : (
            <>
              <p className='text-muted mb-2'>
                Đã quét {overThresholdScannedCount.toLocaleString('vi-VN')} hoá đơn.
                {overThresholdHasMore && ' (Vượt giới hạn quét, có thể còn hoá đơn chưa được kiểm tra — vui lòng thu hẹp bộ lọc.)'}
              </p>
              {overThresholdGroups.length === 0 ? (
                <div className='alert alert-success mb-0'>
                  Không có cặp NCC + người mua + ngày nào vượt {formatVnd(Number(scanThreshold) || OVER_THRESHOLD_AMOUNT)}.
                </div>
              ) : (
                <>
                  <div className='alert alert-warning'>
                    Phát hiện {overThresholdGroups.length} cặp NCC + người mua + ngày có tổng tiền vượt {formatVnd(Number(scanThreshold) || OVER_THRESHOLD_AMOUNT)}.
                  </div>
                  {overThresholdGroups.map((group) => (
                    <div key={group.key} className='mb-4 border rounded p-3'>
                      <div className='d-flex flex-wrap justify-content-between align-items-center mb-2'>
                        <div>
                          <div>
                            <strong>NCC:</strong> {group.sellerName || '-'}{' '}
                            <span className='text-muted'>(MST {group.sellerTaxCode})</span>
                          </div>
                          <div>
                            <strong>Người mua:</strong> {group.buyerName || '-'}{' '}
                            <span className='text-muted'>(MST {group.buyerTaxCode})</span>
                          </div>
                          <div>
                            <strong>Ngày hoá đơn:</strong> {group.dateKey}
                          </div>
                        </div>
                        <div className='text-end'>
                          <Badge bg='danger' className='fs-6'>
                            Tổng: {formatVnd(group.total)}
                          </Badge>
                          <div className='small text-muted mt-1'>
                            {group.invoices.length} hoá đơn
                          </div>
                        </div>
                      </div>
                      <Table size='sm' bordered className='mb-0'>
                        <thead>
                          <tr>
                            <th>Số hoá đơn</th>
                            <th>Ký hiệu</th>
                            <th className='text-end'>Tổng tiền</th>
                            <th>Loại nhiên liệu</th>
                            <th>Biển số</th>
                            <th style={{ width: 200 }}>Thao tác</th>
                          </tr>
                        </thead>
                        <tbody>
                          {group.invoices.map((inv) => {
                            const isMarked = !!inv.MarkedForNoBatching;
                            const isLoading = markingNoBatchInvoiceId === inv._id;
                            return (
                              <tr
                                key={inv._id}
                                style={isMarked ? { backgroundColor: '#ffe5e5' } : undefined}
                              >
                                <td>{inv.InvoiceNo || '-'}</td>
                                <td>{inv.Series || '-'}</td>
                                <td className='text-end'>{formatVnd(inv.TotalAmount)}</td>
                                <td>{inv.DetectedItemType || '-'}</td>
                                <td>{inv.LicensePlate || '-'}</td>
                                <td>
                                  <Button
                                    size='sm'
                                    variant={isMarked ? 'outline-secondary' : 'outline-danger'}
                                    disabled={isLoading}
                                    onClick={() =>
                                      handleMarkInvoiceFromOverThreshold(inv._id, !isMarked)
                                    }
                                  >
                                    {isLoading
                                      ? 'Đang lưu...'
                                      : isMarked
                                      ? 'Bỏ đánh dấu'
                                      : 'Đánh dấu không nhóm'}
                                  </Button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>
                  ))}
                </>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          {overThresholdGroups.length > 0 && (
            <Button variant='success' onClick={exportOverThresholdToExcel}>
              Xuất Excel
            </Button>
          )}
          <Button variant='secondary' onClick={() => setShowOverThresholdModal(false)}>
            Đóng
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal show={!!showSyncModal} onHide={() => setShowSyncModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{MEINVOICE_SYNC_LABEL[showSyncModal]}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Label>Từ ngày</Form.Label>
          <Form.Control
            type='date'
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <Form.Label className='mt-3'>Đến ngày (bao gồm)</Form.Label>
          <Form.Control
            type='date'
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setShowSyncModal(false)}>
            Đóng
          </Button>
          <Button
            variant='primary'
            onClick={() => syncInvoices(showSyncModal)}
            disabled={syncing}
          >
            {syncing ? 'Đang đồng bộ...' : 'Bắt đầu đồng bộ'}
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal
        show={showExportModal}
        onHide={() => setShowExportModal(false)}
        size='lg'
      >
        <Modal.Header closeButton>
          <Modal.Title>Xuất báo cáo hoá đơn</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            {(!filterStartDate || !filterEndDate) && (
              <div className='alert alert-warning py-2 mb-3' role='alert'>
                Cần chọn Từ ngày và Đến ngày ở bộ lọc nâng cao để xuất file.
              </div>
            )}
            <div className='border rounded p-3 bg-light'>
              <h6 className='mb-2'>Thông tin xuất:</h6>
              <p className='mb-1'>
                <strong>Từ ô tìm kiếm:</strong>{' '}
                {searchText?.trim() || 'Không có'}
              </p>
              <p className='mb-1'>
                <strong>Từ ngày:</strong> {filterStartDate || 'Chưa chọn'}
              </p>
              <p className='mb-1'>
                <strong>Đến ngày:</strong> {filterEndDate || 'Chưa chọn'}
              </p>
              <p className='mb-1'>
                <strong>Giáo viên:</strong>{' '}
                {filterDrivingTeacher?.value || 'Tất cả giáo viên'}
              </p>
              <p className='mb-1'>
                <strong>Loại nhiên liệu:</strong>{' '}
                {filterDetectedItemType || 'Tất cả'}
              </p>
              <p className='mb-1'>
                <strong>Trạng thái nhóm:</strong>{' '}
                {filterMarkedForNoBatching === ''
                  ? 'Tất cả'
                  : filterMarkedForNoBatching === 'true'
                  ? 'Đánh dấu không nhóm'
                  : 'Chưa đánh dấu'}
              </p>
              <p className='mb-1'>
                <strong>Loại hoá đơn:</strong>{' '}
                {filterIsSplit === ''
                  ? 'Tất cả'
                  : filterIsSplit === 'true'
                  ? 'Có tách hoá đơn'
                  : 'Không tách'}
              </p>
              <small className='text-warning d-block mt-2'>
                <strong>Lưu ý:</strong> Tối đa 10,000 dòng dữ liệu được xuất.
              </small>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setShowExportModal(false)}>
            Hủy
          </Button>
          <Button
            variant='warning'
            onClick={() => exportInvoices(true)}
            disabled={exporting || !filterStartDate || !filterEndDate}
            title='Xuất 2 sheet: "Chưa chi hết (còn nợ)" và "Chưa chi đồng nào"'
          >
            {exporting ? 'Đang xuất...' : 'Xuất hoá đơn chưa chi'}
          </Button>
          <Button
            variant='success'
            onClick={() => exportInvoices(false)}
            disabled={exporting || !filterStartDate || !filterEndDate}
            title={!filterStartDate || !filterEndDate ? 'Vui lòng chọn khoảng ngày trong bộ lọc nâng cao' : ''}
          >
            {exporting ? 'Đang xuất...' : 'Xuất báo cáo'}
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal
        show={showDetailInvoiceModal}
        onHide={() => setShowDetailInvoiceModal(false)}
        size='lg'
      >
        <Modal.Header closeButton>
          <Modal.Title>Chi tiết hoá đơn</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedInvoice ? (
            <>
              <p>
                <strong>Tình trạng nhóm:</strong>{' '}
                {selectedInvoice.BatchId ? (
                  <span>
                    Đã nhóm (Batch ID:{' '}
                    <a
                      href={`/admin/meinvoice/batch?search=${selectedInvoice.BatchId}`}
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      {selectedInvoice.BatchId}
                    </a>
                    )
                  </span>
                ) : (
                  'Chưa nhóm'
                )}
              </p>
              <p>
                <strong>Số hoá đơn:</strong> {selectedInvoice.InvoiceNo}
              </p>
              <p>
                <strong>Người nhận nhiên liệu:</strong>{' '}
                {selectedInvoice.DrivingTeacher}
              </p>
              <p>
                <strong>Tổng tiền:</strong>{' '}
                <span
                  className={
                    selectedInvoice.TotalAmount > 5000000
                      ? 'text-danger fw-bold'
                      : selectedInvoice.TotalAmount > 1500000
                        ? 'text-warning fw-bold'
                        : ''
                  }
                >
                  {new Intl.NumberFormat('vi-VN', {
                    style: 'currency',
                    currency: 'VND',
                  }).format(selectedInvoice.TotalAmount)}
                  {selectedInvoice.TotalAmount > 5000000
                    ? ' (vượt 5tr)'
                    : selectedInvoice.TotalAmount > 1500000
                      ? ' (vượt 1.5tr)'
                      : ''}
                </span>
              </p>
              <p className='text-success'>
                <strong>Đã chi nhiên liệu:</strong>{' '}
                {new Intl.NumberFormat('vi-VN', {
                  style: 'currency',
                  currency: 'VND',
                }).format(selectedInvoice.InternalPaidAmount)}
              </p>
              <p className='text-warning'>
                <strong>Chưa chi:</strong>{' '}
                {new Intl.NumberFormat('vi-VN', {
                  style: 'currency',
                  currency: 'VND',
                }).format(
                  selectedInvoice.TotalAmount -
                    selectedInvoice.InternalPaidAmount
                )}
              </p>
              {selectedInvoice.ParentInvoiceId && (
                <p>
                  <strong>Hoá đơn gốc:</strong>{' '}
                  <a
                    href={`/admin/meinvoice?search=${selectedInvoice.ParentInvoiceId}`}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    {selectedInvoice.ParentInvoiceId}
                  </a>
                </p>
              )}
              <h5>Hoá đơn đã tách:</h5>
              {selectedInvoice.internalInvoices &&
              selectedInvoice.internalInvoices.length > 0 ? (
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      <th>ID hoá đơn</th>
                      <th>Batch ID</th>
                      <th>Số tiền đã chi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedInvoice.internalInvoices.map((inv, index) => (
                      <tr key={inv._id}>
                        <td>
                          <a
                            href={`/admin/meinvoice?search=${inv._id}`}
                            target='_blank'
                            rel='noopener noreferrer'
                          >
                            {inv._id}
                          </a>
                        </td>
                        <td>{inv.BatchId || 'N/A'}</td>
                        <td>
                          {new Intl.NumberFormat('vi-VN', {
                            style: 'currency',
                            currency: 'VND',
                          }).format(inv.InternalPaidAmount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <p>Không có hoá đơn đã tách.</p>
              )}
              <h5 className='mt-4'>Danh sách hàng hóa:</h5>
              {selectedInvoice.Items && selectedInvoice.Items.length > 0 ? (
                <Table striped bordered hover className='mt-3'>
                  <thead>
                    <tr>
                      <th>Tên mặt hàng</th>
                      <th>Số lượng</th>
                      <th>Đơn giá</th>
                      <th>Thành tiền (chưa VAT)</th>
                      <th>Thành tiền (VAT)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedInvoice.Items.map((item) => (
                      <tr key={item._id}>
                        <td>{item.ItemName}</td>
                        <td>{item.Quantity + ' ' + (item?.UnitName || '')}</td>
                        <td>
                          {new Intl.NumberFormat('vi-VN', {
                            style: 'currency',
                            currency: 'VND',
                          }).format(item.UnitPrice)}
                        </td>
                        <td>
                          {new Intl.NumberFormat('vi-VN', {
                            style: 'currency',
                            currency: 'VND',
                          }).format(item.AmountWithoutVat)}
                        </td>
                        <td>
                          {new Intl.NumberFormat('vi-VN', {
                            style: 'currency',
                            currency: 'VND',
                          }).format(item.Amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <p>Không có mặt hàng nào.</p>
              )}
            </>
          ) : (
            <p>Đang tải...</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          {selectedInvoice && selectedInvoice.MarkedForNoBatching ? (
            <Button variant='outline-danger' onClick={() => handleMarkForNoBatching(selectedInvoice._id, false)}>Huỷ đánh dấu không nhóm hoá đơn này</Button>
          ) : (
            <Button variant='danger' onClick={() => handleMarkForNoBatching(selectedInvoice._id, true)}>Đánh dấu không nhóm hoá đơn này</Button>
          )}
          <Button
            variant='secondary'
            onClick={() => setShowDetailInvoiceModal(false)}
          >
            Đóng
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default MeInvoicePage;
